import type { ArgsFor } from 'ember-modifier'
import Modifier from 'ember-modifier'
import { task } from 'ember-concurrency'
import { registerDestructor } from '@ember/destroyable'
import { waitForPromise } from '@ember/test-waiters'
import type Owner from '@ember/owner'

function cleanup(instance: ImgSizes) {
  void instance.loadSizes.cancelAll()
}

interface ImgSize {
  src: string | URL
  onLoad?: () => void
}

interface ImgSizesSignature {
  Args: {
    Named: {
      sizes: ImgSize[]
    }
  }
}

/**
 * Apply to an img element, along with an array of image size hashes, and this will progressively load each image and
 * call the load callback of each if present.
 *
 * This modifier relies on browser caching - otherwise the "this.element.setAttribute('src', preloadedSrc)" call will
 * download the image again
 *
 * <img
 *  alt="always good to have this"
 *  src=@placeholderSrc
 *  {{img-sizes
 *    sizes=(array
 *      (hash src=@previewSrc onLoad=@onImageLoaded)
 *      (hash src=@fullSrc onLoad=@onFullImageLoaded)
 *    )
 *  }}
 *
 * Note, I did attempt this as a functional modifier - but anytime element.src was set, it would destroy & rebuild the
 * modifier (causing image flickering)
 * />
 */
export class ImgSizes extends Modifier<ImgSizesSignature> {
  constructor(owner: Owner, args: ArgsFor<ImgSizesSignature>) {
    super(owner, args)
    registerDestructor(this, cleanup)
  }

  modify(element: Element, _positional: [], named: { sizes: [ImgSize] }) {
    void this.loadSizes.perform(named.sizes, element)
  }

  loadSizes = task({}, async (sizes: ImgSize[], element: Element) => {
    const taskInstances = sizes.map((size: ImgSize) => this.loadSize.perform(size, element))
    await waitForPromise(Promise.all(taskInstances))
  })

  loadSize = task({ enqueue: true }, async (size: ImgSize, element: Element) => {
    // img.decode() is a native Promise-based API designed for this use case. It avoids the
    // cross-browser inconsistencies of listening for 'load'/'error' events on a shared Image.
    const preloader = new Image()
    preloader.src = size.src.toString()
    try {
      await preloader.decode()
    } catch {
      // load failure is non-fatal — fall through so element.src is still updated
    }
    element.setAttribute('src', size.src.toString())
    size.onLoad?.()
  })
}

export default ImgSizes
